import React, { FC, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Timeout } from 'react-number-format/types/types'

import { useSession } from '@rewardops/auth'
import { Button, useLocale } from '@rewardops/ui'
import { HeroBannerProps, HeroBannerSlide } from '@rewardops/utils'

import { useTranslation } from '~/app/i18n/client'
import {
  TransitionBtn,
  TranstionBtnDirection,
} from '../featured-carousel/featured-carousel-transition-btns'
import { useApi } from '../hooks/use-api'
import { useFilterBySegmentationTags } from '../hooks/use-filter-by-segmentation-tags'
import MapleButton from '../maple/components/button'
import ResponsiveComponent from '../responsive-component'

interface Props {
  content: HeroBannerProps
}

const HeroBanner: FC<Props> = (props: Props) => {
  const { slides: rawSlides, autoslideDelaySeconds } = props?.content

  const INITIAL_SLIDE = 0
  const [currentSlide, setCurrentSlide] = useState<number>(INITIAL_SLIDE)
  const intervalRef = useRef<Timeout | null>(null)
  const [touchStart, setTouchStart] = useState(0)
  const { push } = useRouter()
  const { tenant } = useApi()
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { data: session } = useSession()

  const slides = useFilterBySegmentationTags({
    bannerContent: rawSlides,
  })

  if (!slides?.length) {
    return null
  }

  const hasMoreThanOneSlide = slides?.length > 1

  const goNext = () => {
    if (currentSlide === slides?.length - 1) {
      return setCurrentSlide(INITIAL_SLIDE)
    }

    setCurrentSlide(currentSlide + 1)
  }

  const goPrev = () => {
    if (currentSlide === INITIAL_SLIDE) {
      return setCurrentSlide(slides?.length - 1)
    }

    setCurrentSlide(currentSlide - 1)
  }

  useEffect(() => {
    if (hasMoreThanOneSlide) {
      intervalRef.current = setInterval(goNext, autoslideDelaySeconds + 1000)

      return () => clearInterval(Number(intervalRef?.current))
    }
  }, [currentSlide, hasMoreThanOneSlide])

  const captureStart = (event: any) => {
    setTouchStart(event?.touches?.[0]?.clientX ?? 0)
  }

  const handleSwipe = (event: any) => {
    if (!hasMoreThanOneSlide) return

    const diff = touchStart - event?.changedTouches?.[0]?.clientX

    if (diff > 0) {
      goNext()
    }

    if (diff < 0) {
      goPrev()
    }

    return setTouchStart(event?.changedTouches?.[0]?.clientX)
  }

  const onClick = (currentSlide: HeroBannerSlide | undefined) => {
    if (currentSlide?.link?.isRelativeUrl !== false) {
      return push(currentSlide?.link?.to ?? '/')
    }

    const link = currentSlide?.link?.to?.startsWith('/')
      ? `${tenant?.publicUrl}/${locale}/${currentSlide?.link?.to}`
      : currentSlide?.link?.to

    window.open(link, '_blank')
  }

  const hideEnrollButton =
    session?.user && slides[currentSlide]?.link?.to === '/program-enrollment'

  const slideInfo = (
    <div className="absolute top-[30%] z-10 w-full">
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 text-center">
          <div
            className={
              tenant?.fonts?.secondary
                ? 'text-base font-semibold uppercase'
                : 'text-xs font-medium'
            }
            style={{
              color: slides[currentSlide]?.textColor || '#ffffff',
              ...(tenant?.fonts?.secondary
                ? { fontFamily: 'var(--font-secondary)' }
                : undefined),
            }}
          >
            {slides[currentSlide]?.title}
          </div>
          <div
            className="text-4xl font-semibold md:text-5xl"
            style={{ color: slides[currentSlide]?.textColor || '#ffffff' }}
          >
            {slides[currentSlide]?.description}
          </div>
          {slides[currentSlide]?.subText ? (
            <div
              className="text-base font-medium"
              style={{ color: slides[currentSlide]?.textColor || '#ffffff' }}
            >
              {slides[currentSlide]?.subText}
            </div>
          ) : null}
          {slides[currentSlide]?.link &&
            !hideEnrollButton &&
            !slides[currentSlide]?.showFullBannerAsLink &&
            (tenant?.theme === 'maple' ? (
              <MapleButton
                id={`hero-banner-button-${slides[currentSlide]?.link?.name
                  ?.toLowerCase()
                  .replace(/\s+/g, '-')}`}
                className="h-[56px] w-[212px] shrink-0 rounded-full border-none px-4 text-base font-semibold uppercase tracking-[.2em] text-white"
                size="default"
                onClick={() => onClick(slides[currentSlide])}
              >
                {slides[currentSlide]?.link?.name || ''}
              </MapleButton>
            ) : (
              <Button
                className="h-[56px] w-[212px] rounded-[100px] bg-white px-4 text-base font-semibold text-black hover:text-white hover:no-underline"
                onClick={() => onClick(slides[currentSlide])}
              >
                {slides[currentSlide]?.link?.name}
              </Button>
            ))}
        </div>
      </div>
    </div>
  )

  const sliderDots = (
    <div className="absolute bottom-4 z-10 z-10 w-full sm:bottom-8">
      <div className="flex justify-center">
        {slides?.map((slide, index) => (
          <div
            className={`mx-2 h-2.5 w-2.5 cursor-pointer rounded-full bg-[#D9D9D9] ${
              index === currentSlide ? 'opacity-[.8]' : 'opacity-[.2]'
            }`}
            key={`${slide?.id}-${index}`}
            onClick={() => setCurrentSlide(index)}
          ></div>
        ))}
      </div>
    </div>
  )

  const desktopComponent = (
    <div className="col-span-2 mx-auto mb-[60px] overflow-hidden md:mb-[100px] md:mt-12">
      <div className="relative mx-auto w-[1140px] max-w-[1140px] overflow-hidden rounded-3xl">
        <div className="absolute left-4 top-[50%] z-20 -translate-y-1/2 transform">
          {hasMoreThanOneSlide && (
            <TransitionBtn
              direction={TranstionBtnDirection.Left}
              onClick={goPrev}
              ariaLabel={t('ariaLabel.previousHeroBanner')}
            />
          )}
        </div>

        {slideInfo}

        <div className="flex justify-center">
          <div className="relative h-[490px] w-full">
            <div
              className="flex h-full transition duration-1000 ease-out"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
              }}
            >
              {slides?.map((slide, index) => (
                <React.Fragment key={`${slide?.id}-${index}`}>
                  <img
                    onClick={
                      slide?.showFullBannerAsLink
                        ? () => onClick(slide)
                        : undefined
                    }
                    src={slide?.backgroundImage?.file?.url}
                    className={`h-full w-full shrink-0 rounded-3xl object-cover ${
                      slide?.showFullBannerAsLink ? 'cursor-pointer' : ''
                    }`}
                    alt={
                      slide?.title?.split(' ')?.join('-') || 'Hero banner image'
                    }
                    tabIndex={0}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute right-4 top-[50%] z-20 -translate-y-1/2 transform">
          {hasMoreThanOneSlide && (
            <TransitionBtn
              direction={TranstionBtnDirection.Right}
              onClick={goNext}
              ariaLabel={t('ariaLabel.nextHeroBanner')}
            />
          )}
        </div>

        {slides?.length > 1 ? sliderDots : null}
      </div>
    </div>
  )

  const mobileComponent = (
    <div className="col-span-2 mx-auto mb-[60px] overflow-hidden md:mb-[100px]">
      <div className="relative mx-auto w-screen max-w-full overflow-hidden">
        {slideInfo}

        <div className="relative aspect-[4/3] w-full">
          <div
            className="flex h-full transition duration-300 ease-out"
            style={{
              transform: `translateX(-${currentSlide * 100}%)`,
            }}
            id="mobile-hero-banner-container"
            onTouchStart={captureStart}
            onTouchMove={handleSwipe}
          >
            {slides?.map((slide, index) => (
              <React.Fragment key={`${slide?.id}-${index}`}>
                <img
                  src={
                    slide?.backgroundImageMobile
                      ? slide?.backgroundImageMobile?.file?.url
                      : slide?.backgroundImage?.file?.url
                  }
                  className="h-full w-full shrink-0 object-cover"
                  alt={t('ariaLabel.heroImageBanner')}
                />
              </React.Fragment>
            ))}
          </div>
        </div>

        {slides?.length > 1 ? sliderDots : null}
      </div>
    </div>
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export { HeroBanner }
